/**
 * Shared SSE event handler — maps XP events to toast notifications.
 *
 * Used by both the production hook and the Storybook demo so toast
 * logic stays in one place.
 */

import {
  GamificationDailyLoginToast,
  getDailyLoginToastContent,
  type DailyLoginXpEvent,
} from '~/components/ui/gamification/toasts/gamification-daily-login-toast';
import { GamificationLevelUpToast } from '~/components/ui/gamification/toasts/gamification-level-up-toast';
import { showCustomToast } from '~/components/ui/sonner';
import type { XpEventDto } from '~/lib/sse-client';
import { SentryClient } from '~/lib/sentry.client';
import { GamificationEarnXpToast } from '~/components/ui/gamification/toasts/gamification-earn-xp-toast';

export const MAX_EVENT_LOG = 200;

/**
 * Show the appropriate toast for a parsed XP event.
 * Switches on `event.source.type` — the API's own discriminator.
 */
export function showXpEventToast(event: XpEventDto): void {
  switch (event.source.type) {
    case 'daily-login': {
      const dailyLoginSource = event.source;
      const { title: titleTemplate, description } = getDailyLoginToastContent(
        event as DailyLoginXpEvent
      );

      showCustomToast(id => (
        <GamificationDailyLoginToast
          titleTemplate={titleTemplate}
          description={description}
          xp={dailyLoginSource.amount}
          toastId={id}
        />
      ));
      break;
    }

    case 'xp-gain': {
      const xpGainSource = event.source;

      showCustomToast(id => (
        <GamificationEarnXpToast
          title={xpGainSource.title}
          description={xpGainSource.body}
          xp={xpGainSource.amount}
          actionType={xpGainSource.actionType}
          toastId={id}
        />
      ));
      break;
    }

    case 'level-up': {
      const levelUpSource = event.source;

      showCustomToast(
        id => (
          <GamificationLevelUpToast
            title={levelUpSource.title}
            currentLevel={levelUpSource.tierReached}
            nextLevel={levelUpSource.nextTier}
            nextLevelXp={levelUpSource.nextTierMinXp}
            toastId={id}
          />
        ),
        { size: 'wide' }
      );
      break;
    }

    default: {
      const sourceType = (event.source as { type: string }).type;
      console.warn('[SSE] Unrecognized source type:', sourceType);
      SentryClient.captureMessage('[SSE] Unrecognized XP event source type', {
        level: 'warning',
        tags: {
          xp_event_source_type: String(sourceType),
        },
        extra: {
          eventId: event.id,
          sourceType,
          source: event.source,
        },
      });
    }
  }
}
